import { type FC, useState } from "react";

import { OptionalNote } from "../OptionalNote";

import { CourseSelectionList } from "./CourseSelectionList";
import { MoveCoursesConfirmationModal } from "./MoveCoursesConfirmationModal";

import { COURSE_GROUPING_ID_QUERY_PARAM, COURSE_ID_QUERY_PARAM } from "components/constants";
import { t } from "i18n/i18n";
import { LegacyTextField } from "mds/components/LegacyTextField";
import { SquareButton } from "mds/components/buttons/SquareButton";
import { storeApi } from "store/index";
import { CourseWithGroupingType } from "store/selectors/types";
import { toastSuccessMessage } from "utils/alerts";
import { ADMIN_COURSE_EVENT_PREFIX } from "utils/amplitude";
import { useQueryParamsActions } from "utils/routing";

type AddCourseGroupingModalProps = {
  courses: CourseWithGroupingType[];
  onClose: () => void;
};

export const AddCourseGroupingModal: FC<AddCourseGroupingModalProps> = ({ courses, onClose }) => {
  const [name, setName] = useState("");
  const [selectedCourseIds, setSelectedCourseIds] = useState<string[]>([]);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const { setQueryParams } = useQueryParamsActions();

  const handleSubmit = () => {
    if (!name.trim()) {
      return;
    }

    // Check if any selected course codes belong to a different grouping
    const selectedCourseIdSet = new Set(selectedCourseIds);
    const coursesFromOtherGroupings = courses.filter(
      (course) => selectedCourseIdSet.has(course.id) && course.grouping_id !== null,
    );

    if (coursesFromOtherGroupings.length > 0) {
      // Show confirmation modal
      setShowConfirmation(true);
      return;
    }

    // No course codes from other groupings, proceed directly
    handleCreateGrouping();
  };

  const handleCreateGrouping = async () => {
    const newGrouping = await storeApi.course_groupings.create({
      course_ids: selectedCourseIds,
      parent_id: null,
      title: name.trim(),
    });

    toastSuccessMessage(t("home.toasts.grouping_created"));
    setQueryParams({
      [COURSE_GROUPING_ID_QUERY_PARAM]: newGrouping.id,
      [COURSE_ID_QUERY_PARAM]: null,
    });
    onClose();
  };

  const handleConfirmationCancel = () => {
    setShowConfirmation(false);
  };

  const handleConfirmationConfirm = async () => {
    await handleCreateGrouping();
  };

  const toggleCourseChecked = (courseId: string) => {
    const newSelectedIds = [...selectedCourseIds];

    const codeIndex = newSelectedIds.indexOf(courseId);
    if (codeIndex > -1) {
      newSelectedIds.splice(codeIndex, 1);
    } else {
      newSelectedIds.push(courseId);
    }

    setSelectedCourseIds(newSelectedIds);
  };

  if (showConfirmation) {
    const selectedCourseIdSet = new Set(selectedCourseIds);
    const coursesFromOtherGroupings = courses.filter(
      (course) => selectedCourseIdSet.has(course.id) && course.grouping_id !== null,
    );

    return (
      <MoveCoursesConfirmationModal
        courses={coursesFromOtherGroupings}
        onCancel={handleConfirmationCancel}
        onConfirm={handleConfirmationConfirm}
      />
    );
  }

  return (
    <div>
      <div className="flex flex-col gap-4">
        <label className="flex flex-col gap-1">
          <div className="font-medium">{t("home.courses.course_grouping_title_label")}</div>
          <LegacyTextField
            eventName={`${ADMIN_COURSE_EVENT_PREFIX}Add Course Grouping`}
            placeholder={t("home.placeholder.course_grouping_name")}
            value={name}
            isEditing
            onChange={(e) => setName(String(e.target.value))}
          />
        </label>

        {courses.length > 0 && (
          <div className="flex flex-col gap-1">
            <div className="flex items-center justify-between font-medium">
              {t("home.courses.add_courses_to_grouping_label")}
              <OptionalNote />
            </div>

            <CourseSelectionList
              courses={courses}
              selectedCourseIds={selectedCourseIds}
              onCourseToggle={toggleCourseChecked}
            />
          </div>
        )}
      </div>

      <div className="mt-8 flex w-full justify-end gap-2">
        <SquareButton kind="tertiary" size="m" title={t("common.cancel")} onClick={onClose}>
          {t("common.cancel")}
        </SquareButton>

        <SquareButton
          disabled={!name.trim()}
          kind="accent"
          size="m"
          title={t("common.add.course_grouping")}
          onClick={handleSubmit}
        >
          {t("common.add.course_grouping")}
        </SquareButton>
      </div>
    </div>
  );
};
